import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Calendar, MapPin, Users, Handshake, GraduationCap, Mic, Lightbulb, Globe, Newspaper, Pause, PlayCircle, RefreshCw, Search, Zap, X } from 'lucide-react';
import SuperKonnectedCarousel from './SuperKonnectedCarousel';
import { networkClubNewsService } from '../services/networkClubNewsService';
import { 
  NetworkClubNews, 
  NewsFilterTab, 
  NEWS_TAB_CONFIG, 
  SCOPE_BADGE_CONFIG 
} from '../types/networkclub';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getAvatarUrl } from '../utils/avatarUtils';
import { isPhoneViewport } from '../utils/mobileViewport';

// =====================================================
// TYPES
// =====================================================

interface MultiHeaderProps {
  isAdmin?: boolean;
  currentUser?: any;
  userProfile?: any;
  onConnect?: (userId: string) => void;
  /** Force the active tab on mount (used for mobile sub-tab sync) */
  initialTab?: TabType;
}

type TabType = 'videos' | 'events' | 'ondernemers' | 'collaborations' | 'clubnieuws';

interface OnlineUser {
  id: string;
  displayName: string;
  photoURL?: string;
  region?: string;
  isOnline?: boolean;
  lastSeen?: number;
  expertise?: string[];
  workScope?: string;
  country?: string;
}

// =====================================================
// SHARED TAB CAROUSEL COMPONENT
// Same carousel system for all tabs: auto-scrolling,
// drag to scroll, play/pause, gradient edges
// =====================================================

const TabCarousel: React.FC<{
  items: any[];
  renderCard: (item: any, index: number) => React.ReactNode;
  emptyMessage?: string;
}> = ({ items, renderCard, emptyMessage = 'Geen items gevonden' }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [transform, setTransform] = useState(0);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTransform, setDragStartTransform] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(Math.max(15, items.length * 5));
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [isMobile, setIsMobile] = useState(() => isPhoneViewport());
  useEffect(() => {
    const check = () => setIsMobile(isPhoneViewport());
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const duplicatedItems = useMemo(() => {
    if (items.length < 2) return items;
    return [...items, ...items];
  }, [items]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartTransform(transform);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setTransform(dragStartTransform + (e.clientX - dragStartX));
    };
    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStartX, dragStartTransform]);

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-sm">
        <Calendar size={20} className="mb-1 text-gray-300" />
        <span>{emptyMessage}</span>
      </div>
    );
  }

  // ── MOBILE: vertical stacked cards ──────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }}>
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid #F4F2ED',
              width: '100%',
            }}
          >
            {renderCard(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
    <div 
      className="relative overflow-hidden cursor-grab active:cursor-grabbing flex-1"
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <div 
        className="flex h-full items-center py-1 px-3 transition-transform duration-200"
        style={{
          gap: '10px',
          transform: isDragging ? `translateX(${transform}px)` : undefined,
          animation: isDragging 
            ? 'none' 
            : isPlaying && items.length >= 1
              ? `scroll-carousel ${animationSpeed}s linear infinite` 
              : 'none',
          width: 'max-content'
        }}
      >
        {duplicatedItems.map((item, index) => renderCard(item, index))}
      </div>
      
      {/* Controls — show on hover */}
      <div className={`absolute bottom-3 right-6 flex items-center gap-2 z-20 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
        {/* Speed Control */}
        <div className="bg-white/80 backdrop-blur-sm rounded-lg px-2 py-1 flex items-center gap-2 border border-stone-200 shadow-sm">
          <Zap className="w-3.5 h-3.5 text-stone-500" strokeWidth={2} />
          <input
            type="range"
            min="10"
            max="120"
            value={animationSpeed}
            onChange={(e) => { e.stopPropagation(); setAnimationSpeed(Number(e.target.value)); }}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-20 h-1 bg-stone-200 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #8DA8A0 0%, #8DA8A0 ${((animationSpeed - 10) / 110) * 100}%, #E8EDE4 ${((animationSpeed - 10) / 110) * 100}%, #E8EDE4 100%)`
            }}
            aria-label="Animatie snelheid"
          />
          <span className="text-xs text-stone-500 w-8 text-right">{animationSpeed}s</span>
        </div>
        <button 
          className="bg-white/80 backdrop-blur-sm text-stone-500 px-3 py-1.5 rounded-lg text-sm hover:text-stone-700 hover:bg-white transition-all border border-stone-200 shadow-sm"
          onClick={(e) => { e.stopPropagation(); setIsPlaying(!isPlaying); }}
          aria-label={isPlaying ? 'Pauzeren' : 'Afspelen'}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" strokeWidth={2} />
          ) : (
            <PlayCircle className="w-4 h-4" strokeWidth={2} />
          )}
        </button>
        <button 
          className="bg-white/80 backdrop-blur-sm text-stone-500 px-3 py-1.5 rounded-lg text-sm hover:text-stone-700 hover:bg-white transition-all border border-stone-200 shadow-sm"
          onClick={(e) => { e.stopPropagation(); setTransform(0); setIsPlaying(true); }}
          aria-label="Reset"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={2} />
        </button>
        <button 
          className="bg-stone-600/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm hover:bg-stone-700/90 transition-all shadow-sm flex items-center gap-1.5 border border-stone-500/30"
          onClick={(e) => { e.stopPropagation(); setShowBrowseModal(true); }}
          aria-label="Bladeren"
        >
          <Search className="w-4 h-4" strokeWidth={2} />
          <span>Bladeren</span>
        </button>
      </div>
      
      {/* Gradient edges */}
      <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent pointer-events-none" />
    </div>

    {/* Browse Modal */}
    {showBrowseModal && (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
        onClick={() => setShowBrowseModal(false)}
      >
        <div 
          className="bg-white rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Overzicht ({items.length} items)</h3>
            <button 
              onClick={() => setShowBrowseModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-4 overflow-y-auto max-h-[calc(80vh-60px)]">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((item, index) => (
                <div key={index} className="transform scale-100">
                  {renderCard(item, index)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// =====================================================
// INFO ACCORDION ITEM
// =====================================================

const InfoAccordionItem: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-[#0A2342]">{title}</span>
        <svg 
          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} 
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul className="px-4 pb-2.5 space-y-1.5">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
              <span className="mt-1 w-1 h-1 rounded-full bg-[#7A9488] flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// =====================================================
// MAIN COMPONENT
// =====================================================

const MultiHeader: React.FC<MultiHeaderProps> = ({ 
  isAdmin = false, 
  currentUser, 
  userProfile,
  onConnect,
  initialTab,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(initialTab ?? 'videos');
  const [isMinimized, setIsMinimized] = useState(false);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [videoSortOrder, setVideoSortOrder] = useState<'relevance' | 'newest'>('relevance');
  const [regionFilter, setRegionFilter] = useState<'all' | 'myRegion'>(() => {
    try {
      return localStorage.getItem('superkonnected_multiheader_region_filter') === 'myRegion' ? 'myRegion' : 'all';
    } catch {
      return 'all';
    }
  });
  const [isMobile, setIsMobile] = useState(() => isPhoneViewport());
  const mobilePageMode = isMobile && initialTab !== undefined;

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setMobileSearchTerm('');
  }, [activeTab]);

  useEffect(() => {
    const check = () => setIsMobile(isPhoneViewport());
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('superkonnected_multiheader_region_filter', regionFilter);
    } catch {}
  }, [regionFilter]);

  // Club Nieuws State
  const [clubNews, setClubNews] = useState<NetworkClubNews[]>([]);
  const [isNewsLoading, setIsNewsLoading] = useState(false);

  // Ondernemers Online State
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);

  // User regions for filtering
  const userRegions = useMemo(() => {
    if (!userProfile) return [];
    return userProfile.selectedRegions || [userProfile.region].filter(Boolean);
  }, [userProfile]);

  // Fetch club news
  useEffect(() => {
    const fetchNews = async () => {
      if (activeTab !== 'clubnieuws') return;
      
      setIsNewsLoading(true);
      try {
        const news = await networkClubNewsService.getAllActiveNews();
        if (userProfile) {
          const relevantNews = networkClubNewsService.getRelevantNewsForUser(news, userProfile);
          setClubNews(relevantNews);
        } else {
          setClubNews(news);
        }
      } catch (error) {
        console.error('Error fetching club news:', error);
        setClubNews([]);
      } finally {
        setIsNewsLoading(false);
      }
    };
    
    fetchNews();
  }, [activeTab, userProfile]);

  // Fetch online users for Ondernemers tab
  useEffect(() => {
    const fetchUsers = async () => {
      if (activeTab !== 'ondernemers' || !currentUser?.uid) return;

      setIsUsersLoading(true);
      try {
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);
        const users: OnlineUser[] = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (docSnap.id === currentUser.uid) return;

          if (regionFilter === 'myRegion') {
            const myScope = userProfile?.onboardingData?.workScope || 'lokaal';
            const otherScope = data.onboardingData?.workScope || 'lokaal';

            if (myScope !== 'internationaal' && otherScope !== 'internationaal') {
              if (myScope === 'nationaal' || otherScope === 'nationaal') {
                const myCountry = userProfile?.onboardingData?.country || 'Nederland';
                const otherCountry = data.onboardingData?.country || 'Nederland';
                if (myCountry !== otherCountry) return;
              } else {
                const myRegion = userProfile?.onboardingData?.region || '';
                const otherRegion = data.onboardingData?.region || '';
                if (myRegion !== otherRegion) return;
              }
            }
          }

          users.push({
            id: docSnap.id,
            displayName: data.displayName || data.email?.split('@')[0] || 'Gebruiker',
            photoURL: data.photoURL,
            region: data.onboardingData?.region,
            isOnline: data.isOnline || false,
            lastSeen: data.lastSeen || Date.now(),
            expertise: data.onboardingData?.expertise || [],
            workScope: data.onboardingData?.workScope,
            country: data.onboardingData?.country
          });
        });

        users.sort((a, b) => {
          if (a.isOnline && !b.isOnline) return -1;
          if (!a.isOnline && b.isOnline) return 1;
          return (b.lastSeen || 0) - (a.lastSeen || 0);
        });

        setOnlineUsers(users.slice(0, 50));
      } catch (error) {
        console.error('Error fetching online users:', error);
      } finally {
        setIsUsersLoading(false);
      }
    };

    fetchUsers();
  }, [activeTab, currentUser, userProfile, regionFilter]);

  const matchesMobileSearch = useCallback((values: unknown[]) => {
    const term = mobileSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return values
      .flatMap((value) => Array.isArray(value) ? value : [value])
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term);
  }, [mobileSearchTerm]);

  // Filtered news based on region
  const filteredNews = useMemo(() => {
    const mappedTab = regionFilter === 'myRegion' ? 'myRegion' : 'all';
    return networkClubNewsService.filterNewsByTab(
      clubNews, mappedTab, userRegions, userProfile?.country || 'Nederland'
    );
  }, [clubNews, regionFilter, userRegions, userProfile?.country]);

  // Format relative date
  const formatRelativeDate = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Vandaag';
    if (diffDays === 1) return 'Gisteren';
    if (diffDays < 7) return `${diffDays}d`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
    return `${Math.floor(diffDays / 30)}m`;
  };

  // Format last seen
  const formatLastSeen = (timestamp: number): string => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (minutes < 1) return 'Online';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}u`;
    return `${Math.floor(hours / 24)}d`;
  };

  // Dummy events data
  const dummyEvents = [
    {
      id: 1,
      title: 'SuperKonnected Network Meetup',
      date: '15 oktober 2025',
      time: '18:00 - 21:00',
      location: 'Amsterdam, Startup Hub',
      attendees: 24,
      type: 'networking',
      region: 'Noord-Holland'
    },
    {
      id: 2,
      title: 'Masterclass: Personal Branding',
      date: '22 oktober 2025',
      time: '14:00 - 17:00',
      location: 'Online via Zoom',
      attendees: 48,
      type: 'workshop',
      region: 'NL'
    },
    {
      id: 3,
      title: 'Podcast Recording Live',
      date: '29 oktober 2025',
      time: '19:00 - 20:30',
      location: 'Rotterdam, Studio Antonio',
      attendees: 15,
      type: 'podcast',
      region: 'Zuid-Holland'
    },
    {
      id: 4,
      title: 'Business Pitch Night',
      date: '5 november 2025',
      time: '18:30 - 22:00',
      location: 'Utrecht, Innovation Center',
      attendees: 32,
      type: 'pitch',
      region: 'Utrecht'
    },
    {
      id: 5,
      title: 'AI & Automation Meetup 2026',
      date: '12 november 2025',
      time: '16:00 - 19:00',
      location: 'Eindhoven, Tech Campus',
      attendees: 56,
      type: 'workshop',
      region: 'Noord-Brabant'
    },
    {
      id: 6,
      title: 'Netwerkbijeenkomst Leeuwarden',
      date: '18 november 2025',
      time: '19:00 - 21:30',
      location: 'Leeuwarden, Business Center',
      attendees: 18,
      type: 'networking',
      region: 'Friesland'
    },
    {
      id: 7,
      title: 'Pitch Night: Zoek je co-founder?',
      date: '25 november 2025',
      time: '18:00 - 21:00',
      location: 'Utrecht, Startup Hub',
      attendees: 40,
      type: 'pitch',
      region: 'Utrecht'
    },
    {
      id: 8,
      title: 'Opening nieuwe locatie in Eindhoven',
      date: '1 december 2025',
      time: '15:00 - 18:00',
      location: 'Eindhoven, Brainport',
      attendees: 65,
      type: 'networking',
      region: 'Noord-Brabant'
    }
  ];

  // Mock Ondernemers data (fallback when Firebase is empty)
  const mockOnlineUsers: OnlineUser[] = [
    { id: 'mock-1', displayName: 'Sarah de Vries', isOnline: true, region: 'Noord-Holland', expertise: ['Marketing', 'Branding'], workScope: 'nationaal', country: 'Nederland', lastSeen: Date.now() },
    { id: 'mock-2', displayName: 'Jan Bakker', isOnline: true, region: 'Zuid-Holland', expertise: ['Software', 'AI'], workScope: 'internationaal', country: 'Nederland', lastSeen: Date.now() },
    { id: 'mock-3', displayName: 'Lisa van Dijk', isOnline: false, region: 'Utrecht', expertise: ['Finance', 'Coaching'], workScope: 'lokaal', country: 'Nederland', lastSeen: Date.now() - 1800000 },
    { id: 'mock-4', displayName: 'Mohammed El Amrani', isOnline: true, region: 'Noord-Brabant', expertise: ['E-commerce', 'Growth'], workScope: 'nationaal', country: 'Nederland', lastSeen: Date.now() },
    { id: 'mock-5', displayName: 'Emma Jansen', isOnline: false, region: 'Gelderland', expertise: ['Design', 'UX'], workScope: 'lokaal', country: 'Nederland', lastSeen: Date.now() - 7200000 },
    { id: 'mock-6', displayName: 'Thomas Mulder', isOnline: true, region: 'Friesland', expertise: ['Consulting', 'Strategie'], workScope: 'nationaal', country: 'Nederland', lastSeen: Date.now() },
    { id: 'mock-7', displayName: 'Fatima Bouhaddou', isOnline: true, region: 'Zuid-Holland', expertise: ['HR', 'Recruitment'], workScope: 'lokaal', country: 'Nederland', lastSeen: Date.now() },
    { id: 'mock-8', displayName: 'Pieter de Groot', isOnline: false, region: 'Overijssel', expertise: ['Sales', 'B2B'], workScope: 'nationaal', country: 'Nederland', lastSeen: Date.now() - 3600000 },
  ];

  // Mock Collaborations data
  const mockCollaborations = [
    { id: 'collab-1', user1: 'Sarah de Vries', user2: 'Jan Bakker', project: 'AI Marketing Tool', description: 'Samen een AI-gedreven marketing platform bouwen voor MKB.', type: 'product', region: 'Noord-Holland', date: '2d' },
    { id: 'collab-2', user1: 'Lisa van Dijk', user2: 'Thomas Mulder', project: 'Startup Coaching Programma', description: 'Gecombineerd coaching en strategie traject voor startups.', type: 'service', region: 'Utrecht', date: '5d' },
    { id: 'collab-3', user1: 'Mohammed El Amrani', user2: 'Emma Jansen', project: 'E-commerce Redesign', description: 'Complete UX redesign voor online webshop platform.', type: 'project', region: 'Noord-Brabant', date: '1w' },
    { id: 'collab-4', user1: 'Fatima Bouhaddou', user2: 'Pieter de Groot', project: 'Sales & HR Masterclass', description: 'Workshop serie over effectief recruiten en verkopen.', type: 'event', region: 'Zuid-Holland', date: '3d' },
    { id: 'collab-5', user1: 'Jan Bakker', user2: 'Thomas Mulder', project: 'Tech Consultancy Platform', description: 'Digitaal platform voor tech consultancy diensten.', type: 'product', region: 'Friesland', date: '6d' },
    { id: 'collab-6', user1: 'Emma Jansen', user2: 'Sarah de Vries', project: 'Brand Identity Workshop', description: 'Design en branding workshop voor ondernemers.', type: 'event', region: 'Gelderland', date: '1d' },
    { id: 'collab-7', user1: 'Pieter de Groot', user2: 'Mohammed El Amrani', project: 'B2B Growth Strategie', description: 'Samenwerking voor B2B groei en e-commerce integratie.', type: 'service', region: 'Overijssel', date: '4d' },
    { id: 'collab-8', user1: 'Lisa van Dijk', user2: 'Fatima Bouhaddou', project: 'HR Finance Tool', description: 'Fintech tool voor HR en salarisadministratie.', type: 'product', region: 'Utrecht', date: '2w' },
  ];

  // Mock Club Nieuws data
  const mockClubNews = [
    { id: 'mn-1', clubName: 'Brabant Ondernemers', title: 'Opening nieuwe locatie in Eindhoven', content: 'Onze nieuwe hub in de Brainport regio is nu open. Kom langs voor een rondleiding en ontmoet andere ondernemers.', targetScope: 'lokaal' as const, targetRegions: ['Noord-Brabant'], createdAt: new Date(Date.now() - 2 * 86400000), linkUrl: '' },
    { id: 'mn-2', clubName: 'Friesland Business Network', title: 'Netwerkbijeenkomst Leeuwarden', content: 'Maandelijkse netwerkbijeenkomst in het centrum van Leeuwarden. Gratis voor leden.', targetScope: 'lokaal' as const, targetRegions: ['Friesland'], createdAt: new Date(Date.now() - 6 * 86400000), linkUrl: '' },
    { id: 'mn-3', clubName: 'Utrecht Startup Community', title: 'Pitch Night: Zoek je co-founder?', content: 'Pitch je idee en vind je perfecte co-founder. 3 minuten per pitch. Schrijf je nu in!', targetScope: 'lokaal' as const, targetRegions: ['Utrecht'], createdAt: new Date(Date.now() - 7 * 86400000), linkUrl: '' },
    { id: 'mn-4', clubName: 'Tech Founders NL', title: 'AI & Automation Meetup 2026', content: 'Ontdek hoe AI jouw business kan transformeren. Demo sessies en networking.', targetScope: 'nationaal' as const, targetRegions: [], createdAt: new Date(Date.now() - 3 * 86400000), linkUrl: '' },
    { id: 'mn-5', clubName: 'Amsterdam Business Club', title: 'Groeistrategie voor Scale-ups', content: 'Expert panel over schaalbare groeistrategieen. Met sprekers van Adyen en Booking.', targetScope: 'lokaal' as const, targetRegions: ['Noord-Holland'], createdAt: new Date(Date.now() - 1 * 86400000), linkUrl: '' },
    { id: 'mn-6', clubName: 'Rotterdam Ventures', title: 'Haven Innovatie Challenge', content: 'Nieuwe innovatie challenge voor startups in de logistiek en haven sector.', targetScope: 'lokaal' as const, targetRegions: ['Zuid-Holland'], createdAt: new Date(Date.now() - 4 * 86400000), linkUrl: '' },
    { id: 'mn-7', clubName: 'Digital Nomads NL', title: 'Remote Work Conference 2026', content: 'Grootste remote work conferentie van Nederland. Online en offline deelname mogelijk.', targetScope: 'nationaal' as const, targetRegions: [], createdAt: new Date(Date.now() - 5 * 86400000), linkUrl: '' },
    { id: 'mn-8', clubName: 'Groningen Startup Hub', title: 'Student Entrepreneur Awards', content: 'Jaarlijkse prijs voor de beste student-ondernemer. Nomineer jezelf of een ander!', targetScope: 'lokaal' as const, targetRegions: ['Groningen'], createdAt: new Date(Date.now() - 8 * 86400000), linkUrl: '' },
  ];

  // Use mock data as fallback for Ondernemers
  const displayUsers = onlineUsers.length > 0 ? onlineUsers : mockOnlineUsers;

  // Use mock data as fallback for Club Nieuws
  const displayNews = filteredNews.length > 0 ? filteredNews : mockClubNews;
  const searchedEvents = useMemo(() => dummyEvents.filter((event) => matchesMobileSearch([
    event.title,
    event.date,
    event.time,
    event.location,
    event.region,
    event.type,
  ])), [dummyEvents, matchesMobileSearch]);
  const searchedUsers = useMemo(() => displayUsers.filter((item) => matchesMobileSearch([
    item.displayName,
    item.region,
    item.country,
    item.workScope,
    item.expertise,
  ])), [displayUsers, matchesMobileSearch]);
  const searchedCollaborations = useMemo(() => mockCollaborations.filter((collab) => matchesMobileSearch([
    collab.user1,
    collab.user2,
    collab.project,
    collab.description,
    collab.type,
    collab.region,
  ])), [mockCollaborations, matchesMobileSearch]);
  const searchedNews = useMemo(() => (displayNews as any[]).filter((news) => matchesMobileSearch([
    news.clubName,
    news.title,
    news.content,
    news.targetScope,
    news.targetRegions,
  ])), [displayNews, matchesMobileSearch]);

  const tabs: { id: TabType; label: string }[] = [
    { id: 'videos', label: "Video's" },
    { id: 'events', label: 'Events' },
    { id: 'ondernemers', label: 'Ondernemers Online' },
    { id: 'collaborations', label: 'Collaborations' },
    { id: 'clubnieuws', label: 'Club Nieuws' }
  ];

  const infoSections = [
    {
      title: "Video's",
      items: [
        "Video's worden getoond op basis van je regio",
        "Hoe meer je samenwerkt, hoe vaker je video's worden getoond",
        "Admin video's worden altijd prominent weergegeven"
      ]
    },
    {
      title: "Events",
      items: [
        "Ontdek events en workshops in jouw regio",
        "Schrijf je in voor live events en online sessies",
        "Organiseer zelf events voor de community"
      ]
    },
    {
      title: "Ondernemers Online",
      items: [
        "Zie wie er online is en connect direct",
        "Gebruikers worden getoond op basis van overeenkomende interesses"
      ]
    },
    {
      title: "Collaborations",
      items: [
        "Samenwerkingen worden hier uitgelicht met extra zichtbaarheid",
        "Tag andere ondernemers in je posts om samen te werken"
      ]
    },
    {
      title: "Club Nieuws",
      items: [
        "Nieuws en updates van netwerkclubs in jouw regio",
        "Filter op regio om specifiek nieuws te vinden"
      ]
    }
  ];

  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const infoPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (infoPanelRef.current && !infoPanelRef.current.contains(e.target as Node)) {
        setShowInfoPanel(false);
      }
    };
    if (showInfoPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInfoPanel]);

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'networking': return <Users size={12} color="#FFFFFF" strokeWidth={2} />;
      case 'workshop': return <GraduationCap size={12} color="#FFFFFF" strokeWidth={2} />;
      case 'podcast': return <Mic size={12} color="#FFFFFF" strokeWidth={2} />;
      case 'pitch': return <Lightbulb size={12} color="#FFFFFF" strokeWidth={2} />;
      default: return <Calendar size={12} color="#FFFFFF" strokeWidth={2} />;
    }
  };

  const getEventTypeBadge = (type: string) => {
    switch (type) {
      case 'networking': return { label: 'Netwerk', bg: 'bg-green-50', text: 'text-green-700' };
      case 'workshop': return { label: 'Workshop', bg: 'bg-blue-50', text: 'text-blue-700' };
      case 'podcast': return { label: 'Podcast', bg: 'bg-purple-50', text: 'text-purple-700' };
      case 'pitch': return { label: 'Pitch', bg: 'bg-yellow-50', text: 'text-yellow-700' };
      default: return { label: 'Event', bg: 'bg-gray-50', text: 'text-gray-700' };
    }
  };

  const mobileCardClass = 'tab-carousel-card relative w-full min-h-[132px] overflow-hidden bg-white border border-gray-200 rounded-lg p-3 flex flex-col';
  const desktopCardClass = 'tab-carousel-card relative w-52 h-[130px] bg-white border border-gray-200 rounded-lg p-2.5 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer flex flex-col flex-shrink-0';

  // Render region filter tabs (shared across all tabs)
  const renderRegionFilter = () => (
    <div className="flex items-center justify-between px-4 py-1.5 border-b bg-gray-50 flex-shrink-0">
      <div className="flex gap-1">
        <button
          onClick={() => setRegionFilter('all')}
          className={`px-2 py-1 rounded-full text-xs font-medium transition-all
            ${regionFilter === 'all' 
              ? 'bg-stone-50 text-[#4F6660]' 
              : 'text-gray-500 hover:bg-gray-100'
            }`}
        >
          Alles
        </button>
        <button
          onClick={() => setRegionFilter('myRegion')}
          className={`px-2 py-1 rounded-full text-xs font-medium transition-all
            ${regionFilter === 'myRegion' 
              ? 'bg-stone-50 text-[#4F6660]' 
              : 'text-gray-500 hover:bg-gray-100'
            }`}
        >
          Mijn Regio
        </button>
      </div>
      {activeTab === 'videos' && (
        <select
          value={videoSortOrder}
          onChange={(event) => setVideoSortOrder(event.target.value as 'relevance' | 'newest')}
          className="min-h-0 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-[#4F6660] outline-none"
          aria-label="Video's sorteren"
        >
          <option value="relevance">Relevant</option>
          <option value="newest">Nieuwste</option>
        </select>
      )}
    </div>
  );

  const renderMobileSearchBar = () => {
    if (!mobilePageMode) return null;

    return (
      <div className="fixed bottom-[58px] left-0 right-0 z-[9988] border-t border-gray-100 bg-white px-4 py-2">
        <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 shadow-sm">
          <Search size={16} className="flex-shrink-0 text-[#7A9488]" strokeWidth={2} />
          <input
            value={mobileSearchTerm}
            onChange={(event) => setMobileSearchTerm(event.target.value)}
            placeholder="Zoek regio, naam, stad of dorp"
            className="min-h-0 flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none"
            type="search"
            inputMode="search"
          />
          {mobileSearchTerm && (
            <button
              type="button"
              onClick={() => setMobileSearchTerm('')}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-gray-400"
              aria-label="Zoekopdracht wissen"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };

  // =====================================================
  // CARD RENDERERS - Same dimensions (w-48 h-[120px]) for all tabs
  // =====================================================

  // Event Card
  const renderEventCard = (event: any, index: number) => {
    const badge = getEventTypeBadge(event.type);
    return (
      <div 
        key={`event-${event.id}-${index}`}
        className={isMobile ? mobileCardClass : desktopCardClass}
      >
        <div className="flex items-center gap-2 mb-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7A9488] to-[#4F6660] 
                          flex items-center justify-center flex-shrink-0">
            {getEventTypeIcon(event.type)}
          </div>
          <span className="text-[11px] text-gray-500 truncate flex-1 min-w-0">
            {event.type === 'networking' ? 'Networking' : event.type === 'workshop' ? 'Workshop' : event.type === 'podcast' ? 'Podcast' : 'Pitch Event'}
          </span>
          <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.bg} ${badge.text}`}>
            {badge.label}
          </span>
        </div>

        {/* Title */}
        <h4 className="font-bold text-gray-900 text-xs line-clamp-2 mb-1 leading-tight">
          {event.title}
        </h4>

        {/* Date + time */}
        <p className="text-[10px] text-gray-500 line-clamp-1 mb-auto">
          {event.date} &middot; {event.time}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
          <span className="text-[10px] text-gray-400 truncate max-w-[60%] flex items-center gap-0.5">
            <MapPin size={10} /> {event.location.split(',')[0]}
          </span>
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
            <Users size={10} /> {event.attendees}
          </span>
        </div>
      </div>
    );
  };

  // Ondernemers Online Card
  const renderUserCard = (user: OnlineUser, index: number) => {
    return (
      <div 
        key={`user-${user.id}-${index}`}
        onClick={() => onConnect && onConnect(user.id)}
        className={isMobile ? mobileCardClass : desktopCardClass}
      >
        {/* Online status badge */}
        <span className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-1
                        ${user.isOnline ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${user.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
          {user.isOnline ? 'Online' : formatLastSeen(user.lastSeen || Date.now())}
        </span>

        {/* Avatar + name */}
        <div className="flex items-center gap-2 mb-1.5">
          <img 
            src={getAvatarUrl(user.photoURL, user.id, user.displayName, '708259')}
            alt={user.displayName}
            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            onError={(e) => {
              const initials = (user.displayName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
              (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=708259&color=fff&size=48`;
            }}
          />
          <span className="text-[11px] text-gray-700 truncate flex-1 pr-8 font-semibold">
            {user.displayName}
          </span>
        </div>

        {/* Region */}
        <h4 className="font-bold text-gray-900 text-xs line-clamp-1 mb-1 leading-tight">
          {user.region || 'Nederland'}
        </h4>

        {/* Expertise tags */}
        <div className="flex flex-wrap gap-1 mb-auto overflow-hidden" style={{ maxHeight: '22px' }}>
          {(user.expertise || []).slice(0, 3).map((tag, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 bg-stone-50 text-[#4F6660] rounded font-medium">
              {tag}
            </span>
          ))}
          {!user.expertise?.length && (
            <span className="text-[10px] text-gray-400">Ondernemer</span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
          <span className="text-[10px] text-gray-400">
            {user.workScope === 'internationaal' ? '🌍 Internationaal' : user.workScope === 'nationaal' ? '🇳🇱 Nederland' : '📍 Lokaal'}
          </span>
          <span className="text-[10px] text-[#7A9488] font-semibold">
            Connecten →
          </span>
        </div>
      </div>
    );
  };

  // Collaboration Card
  const renderCollaborationCard = (collab: any, index: number) => {
    const typeBadge: Record<string, { label: string; bg: string; text: string }> = {
      product: { label: 'Product', bg: 'bg-blue-50', text: 'text-blue-700' },
      service: { label: 'Service', bg: 'bg-purple-50', text: 'text-purple-700' },
      project: { label: 'Project', bg: 'bg-orange-50', text: 'text-orange-700' },
      event: { label: 'Event', bg: 'bg-green-50', text: 'text-green-700' },
    };
    const badge = typeBadge[collab.type] || typeBadge.project;
    return (
      <div 
        key={`collab-${collab.id}-${index}`}
        className={isMobile ? mobileCardClass : desktopCardClass}
      >
        <div className="flex items-center gap-2 mb-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7A9488] to-[#4F6660] 
                          flex items-center justify-center flex-shrink-0">
            <Handshake size={12} className="text-white" />
          </div>
          <span className="text-[11px] text-gray-500 truncate flex-1 min-w-0">
            {collab.user1} &amp; {collab.user2}
          </span>
          <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.bg} ${badge.text}`}>
            {badge.label}
          </span>
        </div>

        {/* Project title */}
        <h4 className="font-bold text-gray-900 text-xs line-clamp-1 mb-0.5 leading-tight">
          {collab.project}
        </h4>

        {/* Description */}
        <p className="text-[10px] text-gray-500 line-clamp-2 mb-auto leading-snug">
          {collab.description}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
            <MapPin size={10} /> {collab.region}
          </span>
          <span className="text-[10px] text-gray-400">
            {collab.date}
          </span>
        </div>
      </div>
    );
  };

  // Club Nieuws Card
  const renderNewsCard = (news: any, index: number) => {
    const scopeBadgeMap: Record<string, { bg: string; text: string }> = {
      lokaal: { bg: 'bg-green-50', text: 'text-green-700' },
      regionaal: { bg: 'bg-blue-50', text: 'text-blue-700' },
      nationaal: { bg: 'bg-orange-50', text: 'text-orange-700' },
      internationaal: { bg: 'bg-purple-50', text: 'text-purple-700' },
    };
    const badge = news.targetScope && SCOPE_BADGE_CONFIG[news.targetScope as keyof typeof SCOPE_BADGE_CONFIG]
      ? SCOPE_BADGE_CONFIG[news.targetScope as keyof typeof SCOPE_BADGE_CONFIG]
      : scopeBadgeMap[news.targetScope] || scopeBadgeMap.lokaal;
    return (
      <div 
        key={`news-${news.id}-${index}`}
        onClick={() => news.linkUrl && window.open(news.linkUrl, '_blank')}
        className={isMobile ? mobileCardClass : desktopCardClass}
      >
        <div className="flex items-center gap-2 mb-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7A9488] to-[#4F6660] 
                          flex items-center justify-center flex-shrink-0">
            <Newspaper size={12} color="#FFFFFF" strokeWidth={2} />
          </div>
          <span className="text-[11px] text-gray-500 truncate flex-1 min-w-0">
            {news.clubName}
          </span>
          {regionFilter === 'all' && (
            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5 ${badge.bg} ${badge.text}`}>
              {news.targetScope === 'internationaal' 
                ? <Globe size={9} /> 
                : <MapPin size={9} />}
              {news.targetScope === 'nationaal' ? 'NL' : news.targetScope === 'internationaal' ? 'Int.' : 'Lokaal'}
            </span>
          )}
        </div>
        
        {/* Title */}
        <h4 className="font-bold text-gray-900 text-xs line-clamp-2 mb-0.5 leading-tight">
          {news.title}
        </h4>
        
        {/* Content preview */}
        <p className="text-[10px] text-gray-500 line-clamp-2 mb-auto leading-snug">
          {news.content}
        </p>
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
            {news.targetScope === 'internationaal' ? <Globe size={10} /> : <MapPin size={10} />}
            {news.targetScope === 'nationaal' 
              ? 'NL' 
              : news.targetScope === 'internationaal'
                ? 'Int.'
                : news.targetRegions?.[0]?.substring(0, 12) || 'Lokaal'}
          </span>
          <span className="text-[10px] text-gray-400">
            {formatRelativeDate(news.createdAt)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={infoPanelRef}>
      {/* Shared carousel animation keyframe */}
      <style>{`
        @keyframes scroll-carousel {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Info Card - rendered outside overflow-hidden container */}
      {showInfoPanel && (
        <div className="absolute right-10 top-12 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <span className="text-sm font-semibold text-[#0A2342]">Hoe werkt het?</span>
            <button 
              onClick={() => setShowInfoPanel(false)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            >
              ×
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
            {infoSections.map((section) => (
              <InfoAccordionItem key={section.title} title={section.title} items={section.items} />
            ))}
          </div>
        </div>
      )}

    <div
      className={`bg-white overflow-hidden ${mobilePageMode ? 'relative h-full min-h-0 rounded-none flex flex-col' : `rounded-xl ${isMinimized ? 'h-[72px]' : ''}`}`}
      style={mobilePageMode ? {} : { border: '1px solid #F0EEE9', boxShadow: '0 1px 6px rgba(0,0,0,0.018)' }}
    >
      {/* Tabs Navigation */}
      {!mobilePageMode && <div className={`${isMinimized ? '' : ''}`} style={{ borderBottom: isMinimized ? 'none' : '1px solid #F4F2ED' }}>
        <div className={`relative flex w-full items-center ${isMinimized ? 'h-[72px]' : ''}`}>
          <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-x-contain [-webkit-overflow-scrolling:touch] md:overflow-x-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max max-w-none flex-nowrap items-center justify-start px-4 pr-14 [word-break:keep-all] md:w-full md:max-w-full md:justify-center md:pr-4">
              {tabs.map((tab) => (
                <div key={tab.id} className="relative flex shrink-0 items-center">
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className="relative whitespace-nowrap px-4 py-5 text-sm transition-colors"
                    style={{
                      fontWeight: activeTab === tab.id ? 500 : 400,
                      color: activeTab === tab.id ? '#3C4F49' : '#9BA8A3',
                      letterSpacing: '0.005em'
                    }}
                  >
                    {tab.label}
                    {activeTab === tab.id && !isMinimized && (
                      <span 
                        className="absolute bottom-0 left-0 right-0"
                        style={{ 
                          height: '2px',
                          background: 'linear-gradient(90deg, #8DA8A0 0%, #A5BDB5 100%)',
                          borderRadius: '1px'
                        }}
                      />
                    )}
                  </button>
                </div>
              ))}

              {/* Info Button */}
              <button
                data-info-spot="carousel"
                onClick={() => setShowInfoPanel(!showInfoPanel)}
                className="homepage-info-button ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors"
                style={{
                  backgroundColor: showInfoPanel ? '#8DA8A0' : 'rgba(147,174,163,0.12)',
                  color: showInfoPanel ? '#FFFFFF' : '#7A9488'
                }}
                title="Informatie over alle onderdelen"
              >
                i
              </button>
            </div>
          </div>

          {/* Minimize/Maximize Button */}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label={isMinimized ? 'Maximaliseer' : 'Minimaliseer'}
            title={isMinimized ? 'Maximaliseer' : 'Minimaliseer'}
          >
            {isMinimized ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
        </div>
      </div>}

      {/* Content Area - Fixed height on desktop only */}
      {!isMinimized && (
        <div className={mobilePageMode ? 'flex-1 min-h-0 overflow-hidden' : 'overflow-hidden md:h-[195px]'}>
          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="h-full min-h-0 overflow-hidden flex flex-col">
              {renderRegionFilter()}
              <div className={mobilePageMode ? 'flex-1 min-h-0 overflow-y-auto pb-[50px]' : 'flex-1 overflow-hidden'}>
                <SuperKonnectedCarousel isAdmin={isAdmin} regionFilter={regionFilter} searchQuery={mobileSearchTerm} displaySortOrder={videoSortOrder} />
              </div>
            </div>
          )}

          {/* Events Tab - NOW A CAROUSEL (same as Club Nieuws) */}
          {activeTab === 'events' && (
            <div className="h-full min-h-0 overflow-hidden flex flex-col">
              {renderRegionFilter()}
              <div className={mobilePageMode ? 'flex-1 min-h-0 overflow-y-auto pb-[50px]' : 'flex-1 overflow-hidden'}>
                <TabCarousel 
                  items={searchedEvents}
                  renderCard={renderEventCard}
                  emptyMessage="Geen events gevonden"
                />
              </div>
            </div>
          )}

          {/* Ondernemers Online Tab - Carousel with mock fallback */}
          {activeTab === 'ondernemers' && (
            <div className="h-full min-h-0 overflow-hidden flex flex-col">
              {renderRegionFilter()}
              {isUsersLoading ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Ondernemers laden...
                </div>
              ) : (
                <div className={mobilePageMode ? 'flex-1 min-h-0 overflow-y-auto pb-[50px]' : 'flex-1 overflow-hidden'}>
                  <TabCarousel 
                    items={searchedUsers}
                    renderCard={renderUserCard}
                    emptyMessage="Geen ondernemers gevonden in deze categorie"
                  />
                </div>
              )}
            </div>
          )}

          {/* Collaborations Tab - Carousel with mock data */}
          {activeTab === 'collaborations' && (
            <div className="h-full min-h-0 overflow-hidden flex flex-col">
              {renderRegionFilter()}
              <div className={mobilePageMode ? 'flex-1 min-h-0 overflow-y-auto pb-[50px]' : 'flex-1 overflow-hidden'}>
                <TabCarousel 
                  items={searchedCollaborations}
                  renderCard={renderCollaborationCard}
                  emptyMessage="Geen collaborations gevonden"
                />
              </div>
            </div>
          )}

          {/* Club Nieuws Tab - Carousel with mock fallback */}
          {activeTab === 'clubnieuws' && (
            <div className="h-full min-h-0 overflow-hidden flex flex-col">
              {renderRegionFilter()}
              {isNewsLoading ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Nieuws laden...
                </div>
              ) : (
                <div className={mobilePageMode ? 'flex-1 min-h-0 overflow-y-auto pb-[50px]' : 'flex-1 overflow-hidden'}>
                  <TabCarousel 
                    items={searchedNews}
                    renderCard={renderNewsCard}
                    emptyMessage="Geen nieuws in deze categorie"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {renderMobileSearchBar()}
    </div>
    </div>
  );
};

export default MultiHeader;
